import { fromPmMark, fromPmNode, fromProseMirror } from "@handlewithcare/remark-prosemirror";
import { type Element } from "hast";
import { toHtml } from "hast-util-to-html";
import type { Html, PhrasingContent } from "mdast";
import { type Node as PmNode } from "prosemirror-model";

import { schema } from "../prosemirror-schema";

export function mdastFromProseMirror(node: PmNode) {
	return fromProseMirror(node, {
		schema,
		nodeHandlers: {
			compute_cell: fromPmNode("code", (node) => {
				const id = node.attrs["id"];
				const cellName = node.attrs["cellName"];

				return {
					lang: "JavaScript",
					meta: `cellId:${id} cellName:${cellName}`,
					value: node.textContent,
				};
			}),
			blockquote: fromPmNode("blockquote"),
			code_block: fromPmNode("code", (node) => ({
				lang: node.attrs["language"],
				meta: node.attrs["meta"],
				value: node.textContent,
			})),
			heading: fromPmNode("heading", (node) => ({ depth: node.attrs["level"] })),
			horizontal_rule: fromPmNode("thematicBreak"),
			to_do_item: fromPmNode("listItem", (node) => ({
				data: {
					depth: node.attrs["indent"],
				},
				checked: node.attrs["status"] === "completed",
			})),
			list_item: fromPmNode("listItem", (node) => ({
				data: {
					depth: node.attrs["indent"],
					bullet: node.attrs["bullet"],
				},
			})),
			paragraph(node, _, state) {
				const children = state.all(node) as PhrasingContent[];
				if (children.length === 0) {
					children.push({
						type: "html",
						value: "<br>",
					});
				}
				return {
					type: "paragraph",
					children,
				};
			},
			image(node) {
				return {
					type: "paragraph",
					children: [
						{
							type: "html",
							value: toHtml({
								type: "root",
								children: [
									{
										type: "element",
										tagName: "img",
										properties: {
											src:
												node.attrs["src"] ??
												(node.attrs["file"]
													? `related/${node.attrs["file"]}`
													: ""),
											...(node.attrs["alt"] && { alt: node.attrs["alt"] }),
											...(node.attrs["width"] && {
												width: node.attrs["width"],
											}),
											...(node.attrs["aspectRatio"] && {
												"style": `aspect-ratio: ${node.attrs["aspectRatio"]}`,
												"data-aspect-ratio": node.attrs["aspectRatio"],
											}),
											...(node.attrs["alignment"] && {
												"data-alignment": node.attrs["alignment"],
											}),
											...(node.attrs["file"] && {
												"data-file": node.attrs["file"],
											}),
										},
										children: [],
									} satisfies Element,
								],
							}),
						} satisfies Html,
					],
				};
			},
			hard_break: fromPmNode("break"),
		},
		markHandlers: {
			em: fromPmMark("emphasis"),
			strong: fromPmMark("strong"),
			strike: fromPmMark("delete"),
			link: fromPmMark("link", (mark) => ({
				url: mark.attrs["href"],
				title: mark.attrs["title"],
			})),
			code(mark, _, children) {
				const value = children[0]?.type === "text" ? children[0].value : "";
				return {
					type: "inlineCode",
					value,
				};
			},
		},
	});
}
